import type {
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  SerializableVector3,
  SnapAnchor,
} from "@project/emerge-types";
import { Html } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { useFrame, useThree } from "@react-three/fiber";
import type React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  BackSide,
  type Box3,
  type Euler,
  type Group,
  Line3,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from "three";
import { useOrbitHoverGate } from "../new/orbitHoverGate";
import type { DragActivation } from "../new/ProductViewerConfigContext";
import { markProductSceneChanged } from "../new/sceneActivity";
import { useDragStore } from "../stores/dragStore";
import { Arrow3D } from "./Arrow3D";
import { CasedLine } from "./CasedLine";
import { DragGuideLines, type GuideSegment } from "./DragGuideLines";
import { DragHighlightGlow } from "./DragHighlightGlow";
import { CASING_OPACITY, DRAG_COLORS, outlineStyle } from "./dragTheme";
import { resolveDragVisuals } from "./dragVisuals";
import {
  measurePartBox,
  meshSignature,
  partReachAlong,
} from "./partGeometryFit";
import {
  areaCenterGuideSegments,
  canAxisCenterSnapStayInBounds,
  DEFAULT_DRAG_GRID_STEP_MM,
  dragGridStepFromModifiers,
  dragSnapThresholdFromModifiers,
  edgeRangeSegments,
  isAxisParallelTo,
  isSurfaceAreaEmpty,
  projectConstrainedDragPosition,
  projectOntoLockedAxis,
  projectOntoSurface,
  projectOntoSurfaceAreas,
  type ResolvedSurfaceArea,
  resolveAreaSnapCenter,
  surfaceRectSegments,
} from "./surfaceDrag";

/** Smallest gap (mm) between the part's own surface and the arrow tail. */
const ARROW_CLEARANCE_MM = 12;

/**
 * Render order of the movement outline. It sits under the centre/diagonal guide
 * lines, and a cased stroke claims two consecutive slots (casing, then core).
 */
const OUTLINE_RENDER_ORDER = 999;

/** Marker spheres and the debug helpers, all above the outline. */
const MARKER_RENDER_ORDER = 1003;

/** How far the dark shell around a snap marker extends past it, in mm. */
const MARKER_CASING_MM = 1.5;

/**
 * A snap point: a coloured sphere inside a slightly larger dark shell.
 *
 * The shell is the marker's half of the same light/dark pairing the guide lines
 * use (see `CasedLine`) — a white idle marker would vanish on the white
 * laminate, and a green one is not far off the mint panel. Drawn back-face-only
 * so the marker itself is not hidden by it: only the rim past the marker's own
 * silhouette ever reaches the screen.
 */
const SnapMarker = ({
  position,
  radius,
  color,
  opacity,
}: {
  position: Vector3;
  radius: number;
  color: number;
  opacity: number;
}) => (
  <group
    position={[position.x, position.y, position.z]}
    {...({ userData: { isDragHelper: true } } as WithUserData)}
  >
    <mesh
      renderOrder={MARKER_RENDER_ORDER}
      {...({ userData: { isDragHelper: true } } as WithUserData)}
    >
      <sphereGeometry args={[radius + MARKER_CASING_MM, 16, 16]} />
      <meshBasicMaterial
        color={DRAG_COLORS.guideCasing}
        opacity={CASING_OPACITY}
        transparent
        depthTest={false}
        side={BackSide}
      />
    </mesh>
    <mesh
      renderOrder={MARKER_RENDER_ORDER + 1}
      {...({ userData: { isDragHelper: true } } as WithUserData)}
    >
      <sphereGeometry args={[radius, 16, 16]} />
      <meshBasicMaterial
        color={color}
        opacity={opacity}
        transparent
        depthTest={false}
      />
    </mesh>
  </group>
);

const subscribeToNothing = () => () => undefined;

/** Max pointer travel in screen pixels for a press to count as a click, not a drag */
const CLICK_MOVE_THRESHOLD_PX = 2;

/**
 * Squared world-distance tolerance (mm²) for deciding that an authoritative
 * build matches the committed final drag position during reconcile. Generous
 * enough to absorb sub-mm build rounding, far below the cursor-distance gap of
 * any stale intermediate build still in flight after pointer-up.
 */
const RECONCILE_MATCH_EPSILON_SQ = 4;

/**
 * Safety fallback (ms): if no build ever commits within
 * `RECONCILE_MATCH_EPSILON_SQ` of the final drag position (e.g. the authoritative
 * value is clamped server-side), drop the optimistic position anyway so the part
 * cannot stay frozen and deaf to future prop updates.
 */
const RECONCILE_TIMEOUT_MS = 1000;

/**
 * Module-level active drag session.
 *
 * A drag is driven by window/canvas pointer listeners, but the `DraggablePart`
 * that started it can be unmounted and immediately remounted mid-drag when its
 * React subtree is transiently rebuilt — an incremental product build can
 * briefly drop and re-add a part (observed for roller parts). If the listeners
 * and drag math lived only on the component, that remount would silently kill
 * the drag ("the part suddenly stops following the mouse").
 *
 * We therefore own the listeners at module scope and snapshot the drag math
 * here, so a freshly mounted instance of the *same* part can adopt the in-flight
 * session and keep dragging without interruption. The listeners also outlive the
 * component, so even if the part is removed for good, the user's pointer-up
 * still ends the drag (the stale handler closure is still callable).
 */
interface DragSessionSnapshot {
  plane: Plane | null;
  offset: Vector3 | null;
  initialPosition: Vector3 | null;
  /** Axis this drag is locked to (arrow drag), in parent-local space. */
  lockedAxis: Vector3 | null;
  pointerDownScreen: { x: number; y: number } | null;
  pointerMoved: boolean;
  lastDragPosition: Vector3 | null;
  lastPointerScreen: { x: number; y: number } | null;
}

/**
 * The parts of a pointer event a drag move actually consumes. Structural, so a
 * real `PointerEvent` satisfies it and the very same handler can be re-run from
 * a cached pointer position when only the Shift modifier changed — see the
 * keyboard effect in `DraggablePart`.
 */
interface DragMoveInput {
  clientX: number;
  clientY: number;
  shiftKey: boolean;
}

function getActiveDragMoveContext(
  isDragging: boolean,
  plane: Plane | null,
  offset: Vector3 | null,
): { plane: Plane; offset: Vector3 } | null {
  if (!isDragging || !plane || !offset) {
    return null;
  }
  return { plane, offset };
}

function markPointerMovedPastClickThreshold(
  event: { clientX: number; clientY: number },
  downScreen: { x: number; y: number } | null,
): boolean {
  if (!downScreen) {
    return false;
  }
  return (
    Math.hypot(event.clientX - downScreen.x, event.clientY - downScreen.y) >=
    CLICK_MOVE_THRESHOLD_PX
  );
}

/**
 * Decides whether a pointer-down may start a drag session. Pure so the gate can
 * be unit-tested without a WebGL context.
 *
 * `activateOnly` parts (e.g. the roller board panel) never move; in direct mode
 * they must not open a session at all, because a session disables the camera
 * controls for the whole gesture and would swallow an orbit drag that merely
 * started on top of them. Their plain click still reaches `onActivate` through
 * the regular click handler. In modifier mode the modifier itself already means
 * "I am not orbiting", so the session (and its click/drag classification) stays.
 */
export function canStartDragFromPress(
  event: { button: number; metaKey: boolean; ctrlKey: boolean },
  options: { requireModifier: boolean; activateOnly: boolean },
): boolean {
  if (event.button !== 0) return false;
  if (options.requireModifier) {
    return event.metaKey || event.ctrlKey;
  }
  return !options.activateOnly;
}

interface DragSession {
  partId: string;
  /** Points at the currently owning instance's latest move/up handlers. */
  handlers: {
    onMove: (event: DragMoveInput) => void;
    onUp: (event: PointerEvent) => void;
  };
  /** Drag math so a remounted instance can resume seamlessly. */
  snapshot: DragSessionSnapshot;
  /** Re-enable camera controls etc.; runs exactly once when the drag ends. */
  cleanup: (() => void) | null;
  /** Removes the window/canvas listeners. Called once when the drag truly ends. */
  detach: () => void;
}

let activeDragSession: DragSession | null = null;

/**
 * Single-owner control of the shared canvas cursor.
 *
 * The cursor lives on the one `<canvas>`, but every `DraggablePart` wants to set
 * it (each on hover / drag). Per-instance snapshot-and-restore races across
 * instances: on a hover hand-off, the next part snapshots the cursor while it is
 * still the previous part's override (e.g. "move") and later "restores" that
 * stale value forever — the stuck-cursor bug. Instead we track a single owner:
 * a part sets the cursor only while it owns it, and a release resets the DOM
 * (clearing our inline override so the canvas reverts to its default cursor)
 * *only* when the caller is still the owner. A stale/late instance can therefore
 * neither clobber the current owner's cursor nor resurrect an old value.
 */
let cursorOwnerPartId: string | null = null;

function acquireCanvasCursor(
  el: HTMLElement,
  partId: string,
  cursor: string,
): void {
  cursorOwnerPartId = partId;
  el.style.cursor = cursor;
}

function releaseCanvasCursor(el: HTMLElement, partId: string): void {
  if (cursorOwnerPartId === partId) {
    cursorOwnerPartId = null;
    el.style.cursor = "";
  }
}

/** Type for React Three Fiber components that accept userData */
type WithUserData = {
  userData?: { isDragHelper?: boolean };
};

/** Display values returned from drag update */
export type DragDisplayValues = DragUpdateResult<unknown>["displayValues"];

/** Result returned from onDrag during pointer move */
export type DragOnMoveResult = Pick<
  DragUpdateResult<unknown>,
  "displayValues" | "snappedPosition"
>;

/** Props for the DraggablePart component */
export interface DraggablePartProps {
  /** Unique identifier for this part (used for drag state tracking) */
  partId: string;
  /** The part's current world position */
  position: Vector3;
  /** The part's rotation (Euler angles) */
  rotation?: Euler;
  /** The drag constraint defining movement limits */
  constraint: DragConstraint;
  /** Called continuously during drag with the constrained position. */
  onDrag?: (
    position: Vector3,
    options?: DragUpdateOptions,
  ) => DragOnMoveResult | undefined;
  /** Called when drag ends with the final position (may be snapped) */
  onDragEnd?: (
    position: Vector3,
    snappedAnchor?: SnapAnchor,
    options?: DragUpdateOptions,
  ) => void;
  /** Called when drag starts - return cleanup function to re-enable controls */
  onDragStart?: () => (() => void) | undefined;
  /** Called on a modifier-click that did not turn into a drag (open part menu) */
  onActivate?: () => void;
  /** Children to render (the actual part) */
  children: React.ReactNode;
  /** Whether dragging is enabled (default: true) */
  enabled?: boolean;
  /**
   * How this part is activated for dragging (default: `"modifier"`).
   *
   * `"modifier"` gates the drag behind ⌘/Ctrl and only reveals the movement
   * area, direction arrows and guide lines while the key is held over the part.
   * `"direct"` marks the part as one of those the user is currently placing:
   * it outlines itself on the case - a thin rim that also shows through the
   * case - so it can be found without hunting, even on the far side, and a
   * plain click on it fires `onActivate`. The outline does not show through
   * the other parts of the same option, though: the mirrored twin of a handle
   * hides it like any solid object would, since the two belong together and
   * one shining through the other reads as a defect. The full glow is left for
   * the pointer, and the arrows for `selected` - which is also what a plain
   * press has to find before it drags. Where it may go stays under the
   * pointer of the selected part: the movement area and the guide lines
   * appear on hover and while dragging. See `resolveDragVisuals`.
   */
  dragActivation?: DragActivation;
  /**
   * The host is pointing this part out - its row is hovered in a list. Draws
   * the full glow through the case, whether or not the part is armed, without
   * changing how a press on it behaves. Its armed siblings keep their outline.
   */
  pointedOut?: boolean;
  /**
   * The host has selected this part - it is the one being worked on. Draws
   * its direction arrows, which are also the handles for an axis-locked drag,
   * and in `"direct"` mode it is the one part a plain press drags.
   */
  selected?: boolean;
  /**
   * Reports the pointer entering (`true`) and leaving (`false`) the part, once
   * per boundary crossing, so a host can mirror the hover in its own UI.
   */
  onHoverChange?: (hovered: boolean) => void;
  /** Whether to show hover highlight (default: true) */
  showHoverHighlight?: boolean;
  /** Show debug visuals for constraint bounds (default: false) */
  debug?: boolean;
  /** If true, do not update local state during drag; rely on parent to update position prop (default: false) */
  controlled?: boolean;
}

/**
 * Converts a SerializableVector3 tuple to a Three.js Vector3.
 */
function toVector3(v: SerializableVector3): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}

/**
 * Projects a point onto an edge constraint (1D line segment).
 * The edge is defined by bounds.min to bounds.max.
 */
function projectOntoEdge(
  point: Vector3,
  bounds: { min: Vector3; max: Vector3 },
): Vector3 {
  // Create line segment from bounds
  const line = new Line3(bounds.min, bounds.max);
  const projected = new Vector3();
  // true = clamp to line segment (not infinite line)
  line.closestPointToPoint(point, true, projected);
  return projected;
}

/**
 * A diagonal snap line resolved to Three.js objects (segment endpoints).
 */
export interface ResolvedDiagonalSnap {
  start: Vector3;
  end: Vector3;
  key: string;
  label?: string;
}

/**
 * Projects a point onto a (clamped) line segment from `start` to `end`.
 */
function projectOntoSegment(
  point: Vector3,
  start: Vector3,
  end: Vector3,
): Vector3 {
  const line = new Line3(start, end);
  const projected = new Vector3();
  line.closestPointToPoint(point, true, projected);
  return projected;
}

/**
 * Result of resolving where a surface-constrained point snaps to.
 */
interface SurfaceSnapResult {
  /** The snapped in-plane position. */
  position: Vector3;
  /** Per-axis center-snap state (parallel to `axes`). */
  axisSnapState: boolean[];
  /** Per-diagonal snap state (parallel to `diagonals`). */
  diagonalSnapState: boolean[];
}

interface ActiveSurfaceAreaSnapContext {
  planeNormal: Vector3;
  bounds: { min: Vector3; max: Vector3 };
}

/**
 * Resolves surface snapping for a constrained point. A nearby diagonal (45°)
 * line takes precedence and fully fixes the in-plane position; otherwise each
 * in-plane axis snaps to the center independently.
 *
 * A `threshold` of 0 turns snapping off (both tests are strict `<`, so even a
 * point exactly on the center does not snap) - that is how Shift frees the
 * drag. The bounds clamp and the all-false snap state still apply, so the guide
 * lines correctly show themselves as unsnapped.
 *
 * A `lockedAxis` (the user is dragging one of the part's direction arrows)
 * narrows what may snap to what the lock allows: a diagonal or a center line
 * running across the locked axis would pull the part off the line it was
 * promised to stay on, which is the whole point of grabbing an arrow. Only the
 * center snap along the lock itself survives — the part still finds the middle
 * of the panel in the one direction it is free to move in.
 */
function resolveSurfaceSnap(
  point: Vector3,
  axes: Vector3[],
  center: Vector3,
  diagonals: ResolvedDiagonalSnap[],
  threshold: number,
  activeArea?: ActiveSurfaceAreaSnapContext,
  lockedAxis?: Vector3 | null,
): SurfaceSnapResult {
  let bestIdx = -1;
  let bestDist = lockedAxis ? 0 : threshold;
  let bestPoint: Vector3 | null = null;
  diagonals.forEach((snap, i) => {
    const projected = projectOntoSegment(point, snap.start, snap.end);
    const dist = point.distanceTo(projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
      bestPoint = projected;
    }
  });

  if (bestIdx >= 0 && bestPoint) {
    const position = activeArea
      ? projectOntoSurface(bestPoint, activeArea.planeNormal, activeArea.bounds)
      : bestPoint;
    return {
      position,
      axisSnapState: axes.map(() => false),
      diagonalSnapState: diagonals.map((_, i) => i === bestIdx),
    };
  }

  const snapped = point.clone();
  const axisSnapState = axes.map((axis) => {
    // An axis across the lock may not move the part: snapping it would leave
    // the locked line. The axis along the lock snaps as usual.
    if (lockedAxis && !isAxisParallelTo(axis, lockedAxis)) {
      return false;
    }
    const distToCenter = snapped.clone().sub(center).dot(axis);
    const withinThreshold = Math.abs(distToCenter) < threshold;
    const staysInBounds =
      !activeArea ||
      canAxisCenterSnapStayInBounds(
        snapped,
        axis,
        center,
        activeArea.planeNormal,
        activeArea.bounds,
      );
    const isSnapped = withinThreshold && staysInBounds;
    if (isSnapped) {
      snapped.addScaledVector(axis, -distToCenter);
    }
    return isSnapped;
  });
  if (activeArea) {
    snapped.copy(
      projectOntoSurface(snapped, activeArea.planeNormal, activeArea.bounds),
    );
  }
  return {
    position: snapped,
    axisSnapState,
    diagonalSnapState: diagonals.map(() => false),
  };
}

/**
 * Updates the in-flight drag session snapshot so a mid-drag remount of the part
 * resumes from the latest position/threshold state. No-op when no session is
 * active (e.g. legacy uncontrolled usage outside the session flow).
 */
function persistDragSessionSnapshot(
  partId: string,
  finalPos: Vector3,
  pointerMoved: boolean,
  pointerScreen: { x: number; y: number } | null,
): void {
  if (activeDragSession?.partId === partId) {
    activeDragSession.snapshot.lastDragPosition = finalPos.clone();
    activeDragSession.snapshot.pointerMoved = pointerMoved;
    activeDragSession.snapshot.lastPointerScreen = pointerScreen;
  }
}

function booleanArraysEqual(a: boolean[], b: boolean[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function teardownDragSession(session: DragSession): void {
  session.detach();
  session.cleanup?.();
}

/**
 * Routes pointer events to the in-flight drag for as long as it lasts; returns
 * the detach.
 *
 * On the window only, deliberately. A pointermove over the canvas bubbles to the
 * window, so listening on the canvas as well delivers every event of a drag
 * twice — two `getBoundingClientRect` layout flushes, two raycasts, two snap
 * passes and two `handleDragUpdate` calls per frame. The window listener is also
 * the one that keeps a drag alive once the pointer leaves the canvas, which is
 * most of what dragging a part towards an edge does.
 */
export function attachDragPointerListeners(
  onMove: (event: PointerEvent) => void,
  onEnd: (event: PointerEvent) => void,
): () => void {
  globalThis.addEventListener("pointermove", onMove);
  globalThis.addEventListener("pointerup", onEnd);
  globalThis.addEventListener("pointercancel", onEnd);
  return () => {
    globalThis.removeEventListener("pointermove", onMove);
    globalThis.removeEventListener("pointerup", onEnd);
    globalThis.removeEventListener("pointercancel", onEnd);
  };
}

/**
 * Finds the closest snap anchor within the threshold distance. A threshold of 0
 * finds nothing (the test is a strict `<`), which is how Shift frees the drag.
 */
function findSnapAnchor(
  position: Vector3,
  anchors: SnapAnchor[],
  threshold: number,
): SnapAnchor | undefined {
  let closest: SnapAnchor | undefined;
  let closestDistance = threshold;

  for (const anchor of anchors) {
    const anchorPos = toVector3(anchor.position);
    const distance = position.distanceTo(anchorPos);
    if (distance < closestDistance) {
      closest = anchor;
      closestDistance = distance;
    }
  }

  return closest;
}

export interface DragSnapOutcome {
  finalPos: Vector3;
  snapAnchor?: SnapAnchor;
  surfaceSnap?: SurfaceSnapResult;
}

export interface DragSnapContext {
  constraint: Pick<DragConstraint, "type" | "snapAnchors" | "visualOffset">;
  surfaceAxes: Vector3[];
  activeCenter: Vector3;
  diagonalSnaps: ResolvedDiagonalSnap[];
  threshold: number;
  activeAreaIndex: number | null;
  surfaceAreas: ResolvedSurfaceArea[];
  planeNormal: Vector3 | undefined;
  /** Set while an arrow drag runs: only snaps along this axis are allowed. */
  lockedAxis?: Vector3 | null;
}

/**
 * The single snap decision of a drag: surfaces snap to the center cross or a
 * diagonal, edges and the fallback to a discrete anchor. Exported so the rules
 * can be unit-tested against the real implementation rather than a copy of it.
 */
export function resolveDragSnapOutcome(
  constrainedPosition: Vector3,
  ctx: DragSnapContext,
): DragSnapOutcome {
  const {
    constraint,
    surfaceAxes,
    activeCenter,
    diagonalSnaps,
    threshold,
    activeAreaIndex,
    surfaceAreas,
    planeNormal,
    lockedAxis,
  } = ctx;
  if (constraint.type === "surface" && surfaceAxes.length > 0) {
    const activeBounds =
      activeAreaIndex != null && activeAreaIndex >= 0
        ? surfaceAreas[activeAreaIndex]?.bounds
        : undefined;
    const activeArea =
      activeBounds && planeNormal
        ? { planeNormal, bounds: activeBounds }
        : undefined;
    const surfaceSnap = resolveSurfaceSnap(
      constrainedPosition,
      surfaceAxes,
      activeCenter,
      diagonalSnaps,
      threshold,
      activeArea,
      lockedAxis,
    );
    return { finalPos: surfaceSnap.position, surfaceSnap };
  }

  if (constraint.type === "edge") {
    const partCenterPos = constraint.visualOffset
      ? constrainedPosition.clone().add(toVector3(constraint.visualOffset))
      : constrainedPosition;
    const snapAnchor = findSnapAnchor(
      partCenterPos,
      constraint.snapAnchors,
      threshold,
    );
    return {
      finalPos: snapAnchor ? toVector3(snapAnchor.position) : partCenterPos,
      snapAnchor,
    };
  }

  const snapAnchor = findSnapAnchor(
    constrainedPosition,
    constraint.snapAnchors,
    threshold,
  );
  return {
    finalPos: snapAnchor ? toVector3(snapAnchor.position) : constrainedPosition,
    snapAnchor,
  };
}

/**
 * Component that wraps a part and makes it draggable according to its constraint.
 *
 * Features:
 * - Projects pointer movement onto edge (1D) or surface (2D) constraints
 * - Respects movement bounds
 * - Snaps to anchor points when within threshold
 * - Shows visual guides during drag
 */
export const DraggablePart: React.FC<DraggablePartProps> = ({
  partId,
  position,
  rotation,
  constraint,
  onDrag,
  onDragEnd,
  onDragStart,
  onActivate,
  children,
  enabled = true,
  dragActivation = "modifier",
  pointedOut = false,
  selected = false,
  onHoverChange,
  showHoverHighlight = true,
  debug = false,
  controlled = false,
}) => {
  // The part the user is working on drags without a modifier. Being armed
  // (`direct`) marks a part as one of the step's, but only the selected one
  // drags on a plain press: a press on any other armed part is a click that
  // selects it - through `handleClick`, since no session opens - or, if it
  // travels, an orbit, which a session would have swallowed.
  const requireModifier = dragActivation !== "direct" || !selected;

  // Use global drag store to track which part is being dragged
  // Use selector to only re-render when this specific part's drag state changes
  const draggingPartId = useDragStore((state) => state.draggingPartId);
  const isDragActive = useDragStore((state) => state.isDragActive);
  const startDrag = useDragStore((state) => state.startDrag);
  const endDrag = useDragStore((state) => state.endDrag);
  const { camera, gl, scene } = useThree();
  const groupRef = useRef<Group>(null);
  // The glow needs the mounted group as a *value*, not a ref: a highlighted
  // part draws it in the same commit the group is created in, when a ref read
  // during render is still null and nothing would re-render to fix it.
  const [groupNode, setGroupNode] = useState<Group | null>(null);
  const setGroupRef = useCallback((node: Group | null) => {
    groupRef.current = node;
    setGroupNode(node);
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  // While the user orbits the camera, hover is off: parts sweep under the
  // cursor, and every glow they would light mounts materials and mesh clones
  // mid-orbit. The handlers below take no new hover while the gate holds; a
  // hover the part already has is kept but drawn and reported as none. Only a
  // hovered part subscribes to a change, so an orbit re-renders one part, not
  // every one in the viewer.
  const orbitGate = useOrbitHoverGate();
  const orbitingWhileHovered = useSyncExternalStore(
    orbitGate?.subscribe ?? subscribeToNothing,
    () => isHovered && (orbitGate?.isOrbiting() ?? false),
  );
  const hoverShown = isHovered && !orbitingWhileHovered;
  // Whether the drag modifier (⌘/Ctrl) is currently held while hovering. Gates
  // the drag-mode affordances (movement outline, arrow cross, move cursor) so
  // they appear only once the user is actually able to drag — not on bare hover.
  const [modifierHeld, setModifierHeld] = useState(false);
  const [dragPosition, setDragPosition] = useState<Vector3 | null>(null);
  useLayoutEffect(() => {
    void dragPosition;
    markProductSceneChanged(scene);
  }, [scene, dragPosition]);
  const [hoveredAnchor, setHoveredAnchor] = useState<SnapAnchor | undefined>();
  // Which surface center lines (horizontal/vertical middle) the part is
  // currently snapped to, by index into `surfaceAxes`. Drives guide highlight.
  const [snappedAxes, setSnappedAxes] = useState<boolean[]>([]);
  // Which diagonal snap lines the part is currently snapped to, by index into
  // `diagonalSnaps`. Drives the diagonal guide highlight.
  const [snappedDiagonals, setSnappedDiagonals] = useState<boolean[]>([]);
  // Index into `surfaceAreas` of the panel the part is currently over. Drives
  // which per-panel center guide line is highlighted as snapped (per-area mode).
  const [activeAreaIndex, setActiveAreaIndex] = useState(0);
  const [displayValues, setDisplayValues] =
    useState<DragDisplayValues>(undefined);
  // The direction of the arrow the running drag was started from, in the same
  // parent-local space as `position`. Null for a drag started on the part body,
  // which stays free within its constraint. Mirrored into state only so the
  // arrows can render the locked pair; every handler reads the ref, because the
  // Shift replay below re-enters the move handler outside React's flow.
  const [lockedAxis, setLockedAxis] = useState<Vector3 | null>(null);
  const lockedAxisRef = useRef<Vector3 | null>(null);
  // Which arrow the pointer is on, by direction key, for its hover tint.
  const [hoveredArrowKey, setHoveredArrowKey] = useState<string | null>(null);
  // True while the hover this part is showing comes from one of its arrows
  // rather than from its own body. R3F drops the hover record of an object that
  // unmounts under the pointer without firing `pointerleave`, so an arrow that
  // disappears (modifier released, another part starts dragging) would leave
  // `isHovered` stuck on and the part glowing with the pointer in mid-air.
  const hoverViaArrowsRef = useRef(false);
  const isDraggingRef = useRef(false); // Ref for reliable access in event handlers
  const didDragRef = useRef(false); // Track if a drag occurred (to suppress click)
  const pointerDownScreenRef = useRef<{ x: number; y: number } | null>(null); // For click vs drag detection
  const pointerMovedRef = useRef(false); // True once the pointer travelled past the click threshold during this press
  const lastGridStepMmRef = useRef(DEFAULT_DRAG_GRID_STEP_MM);
  /** Last pointer screen position of this drag; lets a Shift change re-run the move in place. */
  const lastPointerScreenRef = useRef<{ x: number; y: number } | null>(null);
  /** Shift state the last move was resolved with, so a repeat keydown is a no-op. */
  const shiftHeldRef = useRef(false);
  /** Last constrained cursor position; used for onDragEnd when rebuild-driven. */
  const lastDragFinalPosRef = useRef<Vector3 | null>(null);
  const initialPositionRef = useRef<Vector3 | null>(null);
  const dragPlaneRef = useRef<Plane | null>(null);
  const offsetRef = useRef<Vector3 | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const raycasterRef = useRef(new Raycaster());
  const hoveredAnchorRef = useRef<SnapAnchor | undefined>(undefined);
  // Controlled mode only: set on pointer up to keep the optimistic drag
  // position until the authoritative rebuild commits a new `position` prop.
  const pendingReconcileRef = useRef(false);
  // The committed final drag position captured on pointer up. Reconcile waits
  // for an authoritative build that matches this (not just any prop change), so
  // a stale in-flight intermediate build cannot snap the part to the wrong spot.
  const finalDragPositionRef = useRef<Vector3 | null>(null);
  const reconcileTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  // Convert constraint bounds to Vector3
  const bounds = useMemo(
    () => ({
      min: toVector3(constraint.bounds.min),
      max: toVector3(constraint.bounds.max),
    }),
    [constraint.bounds],
  );

  const planeNormal = useMemo(
    () =>
      constraint.planeNormal
        ? toVector3(constraint.planeNormal).normalize()
        : undefined,
    [constraint.planeNormal],
  );

  // Center of the movement area (the surface/edge middle). Prefer the
  // buildstep's "Center" anchor (the visual rect/edge center) and fall back to
  // the bounds center, so the guide lines line up with the geometry.
  const center = useMemo(() => {
    const centerAnchor = constraint.snapAnchors.find(
      (a) => a.label === "Center",
    );
    if (centerAnchor) return toVector3(centerAnchor.position);
    return bounds.min.clone().add(bounds.max).multiplyScalar(0.5);
  }, [constraint.snapAnchors, bounds]);

  // For a surface, the two world axes that lie in the plane (the in-plane
  // horizontal/vertical directions). The plane normal is axis-aligned, so the
  // in-plane axes are simply the other two world axes.
  const surfaceAxes = useMemo(() => {
    if (constraint.type !== "surface" || !planeNormal) return [];
    return [
      new Vector3(1, 0, 0),
      new Vector3(0, 1, 0),
      new Vector3(0, 0, 1),
    ].filter((axis) => Math.abs(planeNormal.dot(axis)) < 0.9);
  }, [constraint.type, planeNormal]);

  // Multiple valid panels (drag areas) of the side, when the constraint exposes
  // them. Empty for single-panel/edge constraints (falls back to `bounds`). An
  // area with no room on an in-plane axis is not a panel the part can sit on
  // and is left out, so it is neither drawn nor clamped into.
  const surfaceAreas = useMemo<ResolvedSurfaceArea[]>(() => {
    if (constraint.type !== "surface" || !constraint.areas || !planeNormal) {
      return [];
    }
    return constraint.areas
      .map((area) => ({
        bounds: {
          min: toVector3(area.bounds.min),
          max: toVector3(area.bounds.max),
        },
        center: area.center ? toVector3(area.center) : undefined,
      }))
      .filter((area) => !isSurfaceAreaEmpty(area, planeNormal));
  }, [constraint.type, constraint.areas, planeNormal]);

  // Per-area center snapping ("Relativ zu Korpus"): every panel snaps to its
  // own center. Otherwise ("Relativ zu Case") the side's single global center
  // is the target — where a panel can reach it.
  const perAreaCenterSnap =
    constraint.type === "surface" &&
    constraint.perAreaCenterSnap === true &&
    surfaceAreas.length > 0;

  // The panels the guide lines and center snaps are computed for: the exposed
  // areas, or the whole `bounds` as one pseudo-panel on an unsplit side. One
  // code path for both, so a single-panel side is not a special case anywhere
  // below.
  //
  // The pseudo-panel carries no center of its own: its fallback is the bounds
  // midpoint. Handing it the global anchor would make the fallback the very
  // point it is falling back from.
  const guideAreas = useMemo<ResolvedSurfaceArea[]>(() => {
    if (constraint.type !== "surface") return [];
    if (surfaceAreas.length > 0) return surfaceAreas;
    return [{ bounds }];
  }, [constraint.type, surfaceAreas, bounds]);

  // Where a part on each panel snaps to. Under "Relativ zu Case" that is the
  // global center on every axis where the panel reaches it, and the panel's own
  // center on an axis where it does not (the case center of a side split by the
  // lid cut can lie in the gap between the panels). Under "Relativ zu Korpus"
  // it is always the panel center. The only place the mode matters.
  const areaSnapCenters = useMemo<Vector3[]>(() => {
    if (!planeNormal) return [];
    return guideAreas.map((area) =>
      resolveAreaSnapCenter(
        area,
        planeNormal,
        perAreaCenterSnap ? null : center,
      ),
    );
  }, [guideAreas, planeNormal, perAreaCenterSnap, center]);

  // One center cross per panel, each line spanning that panel through its snap
  // center. Only the panel the part is currently over (`activeAreaIndex`) shows
  // the snapped highlight; the rest stay idle so every panel's center is still
  // indicated.
  //
  // A line drawn *along* axis i is the one the part lies on when its position on
  // the *perpendicular* in-plane axis is centered. `snappedAxes[i]` tracks the
  // center-snap of axis i, so the line along axis i must read the perpendicular
  // axis's snap state (the two in-plane axes, index `len-1-i`) — otherwise the
  // horizontal/vertical highlight is swapped. `areaCenterGuideSegments` keeps
  // a `null` in place of a collapsed axis so the indices still line up.
  const areaGuides = useMemo<
    Array<{ key: string; center: Vector3; segments: GuideSegment[] }>
  >(() => {
    return guideAreas.map((area, areaIdx) => {
      const snapCenter = areaSnapCenters[areaIdx];
      if (!snapCenter) {
        return {
          key: `area_${areaIdx}`,
          center: area.bounds.min,
          segments: [],
        };
      }
      const isActive = areaIdx === activeAreaIndex;
      const segments: GuideSegment[] = [];
      areaCenterGuideSegments(area, snapCenter, surfaceAxes).forEach(
        (segment, axisIdx) => {
          if (!segment) return;
          segments.push({
            start: segment.start,
            end: segment.end,
            snapped:
              isActive &&
              (snappedAxes[surfaceAxes.length - 1 - axisIdx] ?? false),
            key: `area_${areaIdx}_axis_${axisIdx}`,
          });
        },
      );
      return { key: `area_${areaIdx}`, center: snapCenter, segments };
    });
  }, [guideAreas, areaSnapCenters, surfaceAxes, snappedAxes, activeAreaIndex]);

  // Diagonal (45°) snap lines for surface drags, resolved to segment endpoints.
  // A single-panel roller snaps onto its corner diagonal to keep equal margins.
  const diagonalSnaps = useMemo<ResolvedDiagonalSnap[]>(() => {
    if (constraint.type !== "surface" || !constraint.diagonalSnaps) return [];
    return constraint.diagonalSnaps.map((snap, i) => {
      const start = toVector3(snap.origin);
      const dir = toVector3(snap.direction).normalize();
      const end = start.clone().addScaledVector(dir, snap.length);
      return {
        start,
        end,
        key: snap.label ? `${snap.label}_${i}` : `diagonal_${i}`,
        label: snap.label,
      };
    });
  }, [constraint.type, constraint.diagonalSnaps]);

  // Diagonal guide segments spanning each snap line, highlighted when snapped.
  const guideSegments = useMemo<GuideSegment[]>(
    () =>
      diagonalSnaps.map((snap, i) => ({
        start: snap.start,
        end: snap.end,
        snapped: snappedDiagonals[i] ?? false,
        key: snap.key,
      })),
    [diagonalSnaps, snappedDiagonals],
  );

  // Everything the guide layer draws: each panel's center cross plus the
  // diagonal snap lines.
  const allGuideSegments = useMemo<GuideSegment[]>(
    () => [...areaGuides.flatMap((guide) => guide.segments), ...guideSegments],
    [areaGuides, guideSegments],
  );

  // Optimistic / snap-display parts mirror drag locally. Rebuild-driven parts
  // (corner dimension resizes) keep the built `position` prop — the mesh stays
  // on the moving case corner while settings rebuild each frame.
  const useOptimisticDrag = !controlled || constraint.optimistic === true;
  const trackLocalDragPosition =
    constraint.optimistic === true || constraint.snapDisplayDuringDrag === true;

  /**
   * Projects a world point onto the constraint.
   */
  const projectPoint = useCallback(
    (worldPoint: Vector3): Vector3 => {
      if (constraint.type === "edge") {
        return projectOntoEdge(worldPoint, bounds);
      } else if (constraint.type === "surface" && planeNormal) {
        return projectOntoSurface(worldPoint, planeNormal, bounds);
      }
      // Fallback: clamp to bounds
      return worldPoint.clone().clamp(bounds.min, bounds.max);
    },
    [constraint.type, planeNormal, bounds],
  );

  /**
   * Raycast from screen position onto a plane.
   */
  const raycastOntoPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycasterRef.current.setFromCamera(ndc, camera);
      const ray = raycasterRef.current.ray;
      const intersection = new Vector3();
      return ray.intersectPlane(plane, intersection);
    },
    [camera, gl.domElement],
  );

  /** Abandon pending optimistic reconcile (prior controlled drag still settling). */
  const clearPendingReconcile = useCallback(() => {
    if (reconcileTimeoutRef.current !== null) {
      globalThis.clearTimeout(reconcileTimeoutRef.current);
      reconcileTimeoutRef.current = null;
    }
    pendingReconcileRef.current = false;
    finalDragPositionRef.current = null;
    setDragPosition(null);
  }, []);

  /**
   * Global pointer move handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerMove = useCallback(
    (event: DragMoveInput) => {
      const dragMove = getActiveDragMoveContext(
        isDraggingRef.current,
        dragPlaneRef.current,
        offsetRef.current,
      );
      if (!dragMove) {
        return;
      }
      const { plane: dragPlane, offset } = dragMove;

      // Cache what this move resolved with, so a Shift press/release can re-run
      // it in place (the keyboard effect below) instead of waiting for the next
      // pointer move.
      lastPointerScreenRef.current = { x: event.clientX, y: event.clientY };
      shiftHeldRef.current = event.shiftKey;

      // Track whether the pointer ever travelled past the click threshold during
      // this press. Comparing only down-vs-up positions misclassifies a drag that
      // returns to its start as a click.
      const crossedThreshold = markPointerMovedPastClickThreshold(
        event,
        pointerDownScreenRef.current,
      );
      if (crossedThreshold && !pointerMovedRef.current) {
        // Starting a real drag — drop any pending optimistic reconcile from a
        // prior drag. Defer until threshold so a modifier-click to open the part
        // menu right after a drag does not snap back to a stale position prop.
        clearPendingReconcile();
      }
      if (crossedThreshold) {
        pointerMovedRef.current = true;
      }

      // Activate-only parts (e.g. the roller board panel) never move: they only
      // distinguish click from drag so a modifier-click can open their popup.
      // Skip all projection/snapping/onDrag work.
      if (constraint.activateOnly) {
        return;
      }

      // Gate all drag work behind the click threshold. Below it the press is
      // still a candidate click (modifier-click opens the part menu), so running
      // projection/snap/onDrag/setDragPosition would move the part and commit a
      // change on release from a few pixels of hand jitter. Once crossed, the
      // flag latches for the rest of the press so later sub-threshold moves
      // (including returning toward the start) keep updating normally.
      if (!pointerMovedRef.current) {
        return;
      }

      // Raycast pointer position onto drag plane
      const planePoint = raycastOntoPlane(
        event.clientX,
        event.clientY,
        dragPlane,
      );

      if (!planePoint) {
        return;
      }

      // Subtract offset to get the intended part center position (in World Space)
      const newWorldPosition = planePoint.clone().sub(offset);

      // Convert to Local Space (relative to parent, matching 'position' prop space)
      const newLocalPosition = newWorldPosition.clone();
      if (groupRef.current?.parent) {
        groupRef.current.parent.worldToLocal(newLocalPosition);
      }

      // Arrow drag: collapse the point onto the line the grabbed arrow points
      // along, through the position the press found. This runs *before* the
      // constraint clamp so `projectOntoSurface` still receives a point whose
      // surface-normal coordinate is the part's own (it is authoritative, see
      // `surfaceDrag.ts`); it also discards the off-plane component that the
      // camera-facing drag plane of a locked drag introduces.
      const lock = lockedAxisRef.current;
      const lockOrigin = initialPositionRef.current;
      if (lock && lockOrigin) {
        newLocalPosition.copy(
          projectOntoLockedAxis(newLocalPosition, lockOrigin, lock),
        );
      }

      // Project onto constraint. Multi-area surfaces clamp to the nearest panel
      // (the part jumps across the gap); single-area/edge use the legacy path.
      const { constrainedPosition, activeCenter, activeAreaIndex } =
        projectConstrainedDragPosition(newLocalPosition, {
          constraintType: constraint.type,
          planeNormal,
          surfaceAreas,
          areaSnapCenters,
          defaultCenter: center,
          projectPoint,
        });
      if (activeAreaIndex !== null) {
        setActiveAreaIndex((prev) =>
          prev === activeAreaIndex ? prev : activeAreaIndex,
        );
      }

      const { finalPos, snapAnchor, surfaceSnap } = resolveDragSnapOutcome(
        constrainedPosition,
        {
          constraint,
          surfaceAxes,
          activeCenter,
          diagonalSnaps,
          threshold: dragSnapThresholdFromModifiers(event.shiftKey),
          activeAreaIndex,
          surfaceAreas,
          planeNormal,
          lockedAxis: lock,
        },
      );
      if (surfaceSnap) {
        setSnappedAxes((prev) =>
          booleanArraysEqual(prev, surfaceSnap.axisSnapState)
            ? prev
            : surfaceSnap.axisSnapState,
        );
        setSnappedDiagonals((prev) =>
          booleanArraysEqual(prev, surfaceSnap.diagonalSnapState)
            ? prev
            : surfaceSnap.diagonalSnapState,
        );
      }

      const gridStepMm = dragGridStepFromModifiers(event.shiftKey);
      lastGridStepMmRef.current = gridStepMm;

      const dragUpdate = onDrag?.(finalPos, {
        gridStepMm,
      });
      const displayPos =
        trackLocalDragPosition && dragUpdate?.snappedPosition != null
          ? toVector3(dragUpdate.snappedPosition)
          : finalPos;

      lastDragFinalPosRef.current = displayPos.clone();

      // Controlled rebuild-driven parts (corner resizes) keep the built
      // `position` prop. Everything else mirrors locally — including legacy
      // uncontrolled usage, which always followed the cursor via finalPos.
      if (!controlled || trackLocalDragPosition) {
        setDragPosition(displayPos);
      }
      // Keep the session snapshot current so a remount mid-drag resumes from the
      // latest position/threshold state instead of the pointer-down values.
      persistDragSessionSnapshot(
        partId,
        displayPos,
        pointerMovedRef.current,
        lastPointerScreenRef.current,
      );
      setHoveredAnchor(snapAnchor);

      if (dragUpdate?.displayValues) {
        setDisplayValues(dragUpdate.displayValues);
      }
    },
    [
      projectPoint,
      constraint,
      planeNormal,
      surfaceAreas,
      surfaceAxes,
      areaSnapCenters,
      diagonalSnaps,
      center,
      onDrag,
      raycastOntoPlane,
      controlled,
      trackLocalDragPosition,
      partId,
      clearPendingReconcile,
    ],
  );

  // Keep hoveredAnchorRef in sync with state (for use in pointer up handler to avoid stale closure)
  useEffect(() => {
    hoveredAnchorRef.current = hoveredAnchor;
  }, [hoveredAnchor]);

  // Reconcile after a controlled drag ends: drop the optimistic `dragPosition`
  // only once an authoritative build commits a `position` that matches the
  // committed final drag target. Builds are coalesced/RAF-batched, so an older
  // in-flight drag build can land after pointer-up; clearing on the first prop
  // change (any build) would snap the part to that stale intermediate value and
  // cause a visible jump. Matching the final position swaps when the part is
  // already at the committed location -> no visible pop. A timeout fallback (set
  // in pointer-up) guards against a build that settles slightly off-target.
  useEffect(() => {
    if (!pendingReconcileRef.current || isDraggingRef.current) return;
    const finalPos = finalDragPositionRef.current;
    if (
      finalPos &&
      position.distanceToSquared(finalPos) > RECONCILE_MATCH_EPSILON_SQ
    ) {
      // Stale intermediate build (still chasing the final value): keep optimistic.
      return;
    }
    if (reconcileTimeoutRef.current !== null) {
      globalThis.clearTimeout(reconcileTimeoutRef.current);
      reconcileTimeoutRef.current = null;
    }
    pendingReconcileRef.current = false;
    finalDragPositionRef.current = null;
    setDragPosition(null);
  }, [position]);

  /**
   * Global pointer up handler for drag (attached to window during drag).
   * Uses refs instead of state to avoid stale closure issues.
   */
  const handleGlobalPointerUp = useCallback(
    (_event: PointerEvent) => {
      if (!isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = false;
      const wasArrowDrag = lockedAxisRef.current !== null;
      lockedAxisRef.current = null;
      setLockedAxis(null);

      // Treat a modifier-press that never moved past the threshold as a click,
      // not a drag, and open the part's menu instead of committing a position
      // change. Uses the per-frame movement flag (not down-vs-up distance) so a
      // drag that returns to its start still counts as a drag.
      const hadPointerDown = pointerDownScreenRef.current !== null;
      pointerDownScreenRef.current = null;
      const isClick = hadPointerDown && !pointerMovedRef.current;

      // End the module-level session FIRST to prevent any race conditions:
      // remove its window/canvas listeners and re-enable camera controls. This
      // runs correctly even if `this` is a stale (already unmounted) instance
      // whose drag was adopted-then-released during a remount gap.
      const session = activeDragSession;
      activeDragSession = null;
      if (session) {
        session.detach();
        session.cleanup?.();
      }
      cleanupRef.current = null;

      setIsDragging(false);
      endDrag(); // Clear global drag state (with delay for click suppression)
      setDisplayValues(undefined); // Clear display values

      // Use ref for hoveredAnchor to avoid stale closure
      const currentHoveredAnchor = hoveredAnchorRef.current;

      const committedPos = isClick
        ? null
        : (lastDragFinalPosRef.current?.clone() ?? null);
      if (committedPos) {
        onDragEnd?.(committedPos, currentHoveredAnchor, {
          gridStepMm: lastGridStepMmRef.current,
        });
      }

      // In direct mode every plain left-press opens a session, which sets
      // `didDragRef` and makes `handleClick` swallow the DOM click that follows.
      // A press that never moved is a click, so activate from here instead —
      // otherwise clicking a part in placement mode would do nothing at all.
      // Modifier mode is untouched: its clicks still go through `handleClick`.
      //
      // A press on an arrow is excluded: the arrows are a movement affordance
      // and sit off the part, so a press there that happened not to travel far
      // enough is a failed drag, not a request to open the part's menu.
      if (isClick && !requireModifier && !wasArrowDrag) {
        onActivate?.();
      }

      setDragPosition((currentDragPos) => {
        // A press classified as a click (no movement past the threshold) must
        // not commit a position change — even when an earlier controlled drag's
        // optimistic dragPosition is still pending reconcile. Committing here
        // would fire a spurious onDragEnd settings update/rebuild while merely
        // opening the part menu. Leave the pending value untouched so its own
        // reconcile still resolves it.
        if (isClick) {
          return currentDragPos;
        }
        const optimisticPos = currentDragPos ?? committedPos;
        if (controlled && useOptimisticDrag && optimisticPos) {
          // Keep the optimistic position until an authoritative build matching
          // this committed value arrives, then reconcile (see effect above).
          // Clearing immediately would briefly show the previous built position
          // (one build behind) and cause a visible pop.
          pendingReconcileRef.current = true;
          finalDragPositionRef.current = optimisticPos.clone();
          if (reconcileTimeoutRef.current !== null) {
            globalThis.clearTimeout(reconcileTimeoutRef.current);
          }
          reconcileTimeoutRef.current = globalThis.setTimeout(() => {
            reconcileTimeoutRef.current = null;
            if (isDraggingRef.current) {
              return;
            }
            pendingReconcileRef.current = false;
            finalDragPositionRef.current = null;
            setDragPosition(null);
          }, RECONCILE_TIMEOUT_MS);
          return optimisticPos;
        }
        // Rebuild-driven (controlled edge) or legacy uncontrolled: dragPosition
        // is either unused or owned locally; clear so the built position wins.
        return null;
      });
      setHoveredAnchor(undefined);
      setSnappedAxes([]);
      setSnappedDiagonals([]);
      initialPositionRef.current = null;
      dragPlaneRef.current = null;
      offsetRef.current = null;
      lastDragFinalPosRef.current = null;

      // Reset didDragRef after a short delay to allow click event to be suppressed
      globalThis.setTimeout(() => {
        didDragRef.current = false;
      }, 100);
    },
    [
      onDragEnd,
      onActivate,
      requireModifier,
      endDrag,
      controlled,
      useOptimisticDrag,
    ],
  );

  /**
   * Handle click - open the part's settings on a genuine (non-moving) click,
   * and suppress the click that concludes a drag.
   *
   * A plain left-click that did not travel opens the settings menu — no modifier
   * needed. `event.delta` is the pixel distance R3F measured between pointer-down
   * and this click, so a camera orbit (which also drives a click) is rejected by
   * the same threshold used for drag vs. click; an orbit usually also swings the
   * part out from under the cursor, so this handler never even fires. Modifier
   * clicks are handled by the pointer-up path instead (they set `didDragRef` on
   * pointer-down, so they are suppressed here) — the two paths never both fire.
   */
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (didDragRef.current) {
        // Concludes a drag (or a modifier press): don't let it also toggle
        // something (e.g. the lid) or double-open via both paths.
        event.stopPropagation();
        return;
      }
      // Only claim the click when there is a menu to open, so parts without one
      // don't swallow clicks meant for handlers behind them.
      if (onActivate && event.delta <= CLICK_MOVE_THRESHOLD_PX) {
        event.stopPropagation();
        onActivate();
      }
    },
    [onActivate],
  );

  /**
   * Handle pointer down - start drag.
   * By default requires the Command key (⌘) on macOS or Ctrl on Windows/Linux
   * to be held, to prevent accidental part moves while orbiting. Hosts that put
   * the user into an explicit placement mode can drop that gate with
   * `dragActivation="direct"`.
   *
   * `lockAxis` is passed when the press landed on one of the direction arrows
   * rather than on the part: the drag is then confined to that arrow's axis.
   * The gate is the same either way — an arrow advertises a drag, so it must
   * not offer one the part itself would refuse.
   */
  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>, lockAxis?: Vector3) => {
      if (!enabled) return;

      if (
        !canStartDragFromPress(event.nativeEvent, {
          requireModifier,
          activateOnly: constraint.activateOnly === true,
        })
      ) {
        return;
      }
      if (isDraggingRef.current) return;

      // A prior drag can leave orphaned window listeners if pointer-up/cancel
      // was missed (tab switch, OS gesture). Overwriting the session without
      // detach leaks 3 listeners per drag and they keep firing on every move.
      if (activeDragSession) {
        teardownDragSession(activeDragSession);
        activeDragSession = null;
        endDrag();
      }

      event.stopPropagation();

      pointerDownScreenRef.current = {
        x: event.nativeEvent.clientX,
        y: event.nativeEvent.clientY,
      };
      lastPointerScreenRef.current = {
        x: event.nativeEvent.clientX,
        y: event.nativeEvent.clientY,
      };
      shiftHeldRef.current = event.nativeEvent.shiftKey;
      pointerMovedRef.current = false;
      lastDragFinalPosRef.current = null;
      initialPositionRef.current = position.clone();
      const lock = lockAxis ? lockAxis.clone().normalize() : null;
      lockedAxisRef.current = lock;
      setLockedAxis(lock);

      // Start with the panel the part actually sits on, so the first frame's
      // highlight and snap center belong to it rather than to whichever panel
      // happens to be listed first.
      if (planeNormal && surfaceAreas.length > 0) {
        setActiveAreaIndex(
          projectOntoSurfaceAreas(position, planeNormal, surfaceAreas).index,
        );
      }

      // Get true world position for plane calculation
      const worldPosition = new Vector3();
      if (groupRef.current) {
        groupRef.current.getWorldPosition(worldPosition);
      } else {
        worldPosition.copy(position);
      }

      // Determine drag plane normal
      // For surfaces, drag along the surface plane for 1:1 movement
      // For edges or fallback, face the camera to ensure intersection
      const cameraFacingNormal = () =>
        camera.position.clone().sub(worldPosition).normalize();
      let dragPlaneNormal: Vector3;
      if (lock) {
        // Arrow drag: the plane that contains the locked axis and turns as far
        // towards the camera as it can — the best-conditioned plane holding the
        // axis, and the same choice three's own `TransformControls` makes. The
        // surface plane would do for a face-on view but degenerates at a
        // grazing angle, where the ray runs almost parallel to it and the
        // intersection is either wildly far out or missing altogether. Whatever
        // the plane contributes off the locked line is discarded by the
        // projection in the move handler, so only its conditioning matters.
        const worldAxis = lock.clone();
        if (groupRef.current?.parent) {
          worldAxis.transformDirection(groupRef.current.parent.matrixWorld);
        }
        worldAxis.normalize();
        const viewDir = cameraFacingNormal();
        const normal = viewDir
          .clone()
          .addScaledVector(worldAxis, -viewDir.dot(worldAxis));
        // Looking straight down the arrow there is no such plane; any plane
        // through the axis is equally bad, so keep the camera-facing one.
        dragPlaneNormal =
          normal.lengthSq() > 1e-6 ? normal.normalize() : viewDir;
      } else if (constraint.type === "surface" && planeNormal) {
        dragPlaneNormal = planeNormal;
      } else {
        dragPlaneNormal = cameraFacingNormal();
      }

      dragPlaneRef.current = new Plane().setFromNormalAndCoplanarPoint(
        dragPlaneNormal,
        worldPosition,
      );

      // Calculate offset: where on the plane we clicked relative to the part center (in world space)
      const clickPoint = raycastOntoPlane(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        dragPlaneRef.current,
      );

      if (clickPoint) {
        offsetRef.current = clickPoint.clone().sub(worldPosition);
      } else {
        offsetRef.current = new Vector3();
      }

      isDraggingRef.current = true;
      didDragRef.current = true; // Mark that a drag started (to suppress click)
      setIsDragging(true);
      startDrag(partId); // Track which part is dragging in global store

      // Attach the pointer listeners at module scope (not bound to this
      // component instance) so the drag survives an unmount/remount of this
      // part mid-drag. The listeners dispatch to the session's current handlers,
      // which the (re)mounted owner keeps pointed at its own latest closures.
      const detach = attachDragPointerListeners(
        (event) => activeDragSession?.handlers.onMove(event),
        (event) => activeDragSession?.handlers.onUp(event),
      );

      // Call onDragStart (disables camera controls); its cleanup is stored on
      // the session so it runs exactly once when the drag truly ends, even
      // across a mid-drag remount.
      const cleanup = onDragStart?.() ?? null;
      cleanupRef.current = cleanup;

      activeDragSession = {
        partId,
        handlers: {
          onMove: handleGlobalPointerMove,
          onUp: handleGlobalPointerUp,
        },
        snapshot: {
          plane: dragPlaneRef.current,
          offset: offsetRef.current,
          initialPosition: initialPositionRef.current,
          lockedAxis: lock,
          pointerDownScreen: pointerDownScreenRef.current,
          pointerMoved: pointerMovedRef.current,
          lastDragPosition: null,
          lastPointerScreen: lastPointerScreenRef.current,
        },
        cleanup,
        detach,
      };
    },
    [
      enabled,
      position,
      onDragStart,
      camera,
      raycastOntoPlane,
      handleGlobalPointerMove,
      handleGlobalPointerUp,
      constraint.type,
      constraint.activateOnly,
      planeNormal,
      surfaceAreas,
      partId,
      requireModifier,
      startDrag,
      endDrag,
    ],
  );

  // Adopt an in-flight drag session on mount, and decide on unmount whether the
  // drag should survive (this part is being remounted mid-drag) or end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount/unmount only; reading latest refs is desired
  useEffect(() => {
    // If this part already has a drag in flight, the previous instance was
    // unmounted mid-drag (transient rebuild). Resume from the snapshot instead
    // of dropping the drag.
    const session = activeDragSession;
    if (session && session.partId === partId) {
      const { snapshot } = session;
      isDraggingRef.current = true;
      didDragRef.current = true;
      dragPlaneRef.current = snapshot.plane;
      offsetRef.current = snapshot.offset;
      initialPositionRef.current = snapshot.initialPosition;
      lockedAxisRef.current = snapshot.lockedAxis;
      setLockedAxis(snapshot.lockedAxis);
      pointerDownScreenRef.current = snapshot.pointerDownScreen;
      pointerMovedRef.current = snapshot.pointerMoved;
      lastPointerScreenRef.current = snapshot.lastPointerScreen;
      cleanupRef.current = session.cleanup;
      // Take over the live listeners with this instance's handlers.
      session.handlers = {
        onMove: handleGlobalPointerMove,
        onUp: handleGlobalPointerUp,
      };
      setIsDragging(true);
      const resumeLocalDrag =
        !controlled ||
        constraint.optimistic === true ||
        constraint.snapDisplayDuringDrag === true;
      if (snapshot.lastDragPosition && resumeLocalDrag) {
        setDragPosition(snapshot.lastDragPosition.clone());
      }
    }

    return () => {
      // Mid-drag remount: keep the module session + its window listeners alive
      // so the remounted instance can adopt them. Re-enabling controls or
      // detaching here would abort the drag (the historic bug). The user's
      // pointer-up still ends the drag via the persistent listeners even if the
      // part is never remounted.
      if (isDraggingRef.current && activeDragSession?.partId === partId) {
        return;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (reconcileTimeoutRef.current !== null) {
        globalThis.clearTimeout(reconcileTimeoutRef.current);
        reconcileTimeoutRef.current = null;
      }
    };
  }, []); // Empty deps - mount/unmount only

  // Keep the live session pointed at this owner's latest handler closures so the
  // module-level listeners always dispatch to current state (handlers are
  // re-created when their dependencies change).
  useEffect(() => {
    if (activeDragSession?.partId === partId) {
      activeDragSession.handlers = {
        onMove: handleGlobalPointerMove,
        onUp: handleGlobalPointerUp,
      };
    }
  }, [handleGlobalPointerMove, handleGlobalPointerUp, partId]);

  // Shift frees the drag from every snap, but it is otherwise only read on
  // `pointermove` — so pressing or releasing it with the pointer standing still
  // would leave the part snapped and its guide line green until the next mouse
  // move, lying about what a release would commit. Re-running the last move from
  // the cached pointer position re-resolves position, snap state and grid step,
  // so the toggle is instant. That re-run also keeps `lastGridStepMmRef` honest
  // when Shift is released and the mouse let go without moving in between.
  //
  // Keyed on the *value* of `shiftKey` rather than on `e.key`: that ignores key
  // auto-repeat (one rebuild per repeat otherwise), makes every unrelated key a
  // no-op, and still catches Shift being released under another held key.
  //
  // Safe against the click/drag gate: the re-run carries the real cached pointer
  // coordinates, so `markPointerMovedPastClickThreshold` measures exactly what a
  // real move would and a Shift press cannot promote a click into a drag.
  useEffect(() => {
    if (!isDragging) return;
    const syncShift = (e: KeyboardEvent) => {
      if (e.shiftKey === shiftHeldRef.current) return;
      shiftHeldRef.current = e.shiftKey;
      const screen = lastPointerScreenRef.current;
      if (!screen) return;
      handleGlobalPointerMove({
        clientX: screen.x,
        clientY: screen.y,
        shiftKey: e.shiftKey,
      });
    };
    // Focus lost mid-hold: drop the flag so the next real move re-reads it. No
    // re-run — nothing is visible while blurred, and a rebuild there buys
    // nothing.
    const reset = () => {
      shiftHeldRef.current = false;
    };
    globalThis.addEventListener("keydown", syncShift);
    globalThis.addEventListener("keyup", syncShift);
    globalThis.addEventListener("blur", reset);
    return () => {
      globalThis.removeEventListener("keydown", syncShift);
      globalThis.removeEventListener("keyup", syncShift);
      globalThis.removeEventListener("blur", reset);
    };
  }, [isDragging, handleGlobalPointerMove]);

  // Hover tracks pure pointer presence — it must NOT be gated by drag state.
  // The drag runs on window listeners (no pointer capture), so R3F keeps
  // raycasting and fires leave the instant the moving mesh slips out from under
  // the cursor; gating leave on `!isDragging` dropped that event and left the
  // part stuck-hovered after the drag. No drag-time visual reads `isHovered`
  // (guides/anchors/outline key off `isDragging`; the glow is suppressed during
  // a drag), so updating it freely during a drag is safe and keeps it truthful.
  // A camera orbit is different: enter and move take no new hover while the
  // orbit gate holds, and the gate re-runs the hover test once it opens, so
  // the part under the resting cursor still lights up. Leave always clears.

  /**
   * Handle pointer enter - show hover state.
   */
  const handlePointerEnter = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled || orbitGate?.isOrbiting()) return;
      hoverViaArrowsRef.current = false;
      setIsHovered(true);
      // Seed the modifier state from the enter event: a key already held when
      // the pointer arrives won't re-fire keydown, so read it directly here.
      setModifierHeld(event.nativeEvent.metaKey || event.nativeEvent.ctrlKey);
    },
    [enabled, orbitGate],
  );

  /**
   * Handle pointer move - re-assert presence and re-sync the modifier. R3F only
   * fires `enter` on a boundary crossing, so this is what recovers a missed
   * enter/leave (fast drags, dynamic geometry) and a missed key event: any
   * motion over the part corrects both. Same-value setState is a no-op.
   */
  const handlePointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!enabled || orbitGate?.isOrbiting()) return;
      hoverViaArrowsRef.current = false;
      setIsHovered(true);
      setModifierHeld(event.nativeEvent.metaKey || event.nativeEvent.ctrlKey);
    },
    [enabled, orbitGate],
  );

  /**
   * The same hover, entered from one of the arrows instead of from the part.
   *
   * The arrows stand off the part and are a sibling of it in the scene, so
   * without this the pointer travelling from the part to an arrow would end the
   * hover, unmount the arrows in modifier mode, and take the drag handle away
   * exactly as it was being reached for. R3F fires the part's `leave` and this
   * `enter` from the same native move, and React batches the two updates, so
   * the hand-off costs no frame in which the arrows are gone.
   */
  const handleArrowPointerEnter = useCallback(
    (event: ThreeEvent<PointerEvent>, dirKey: string) => {
      if (!enabled || orbitGate?.isOrbiting()) return;
      hoverViaArrowsRef.current = true;
      setIsHovered(true);
      setHoveredArrowKey(dirKey);
      setModifierHeld(event.nativeEvent.metaKey || event.nativeEvent.ctrlKey);
    },
    [enabled, orbitGate],
  );

  const handleArrowPointerLeave = useCallback((dirKey: string) => {
    setHoveredArrowKey((prev) => (prev === dirKey ? null : prev));
  }, []);

  /**
   * Handle pointer leave - hide hover state (always, even mid-drag).
   */
  const handlePointerLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // While hovered, track the drag modifier (⌘/Ctrl) for the stationary case
  // (press/release without moving). Motion-driven re-sync comes from
  // `handlePointerMove`; leaving the part tears this down and clears the flag.
  useEffect(() => {
    if (!isHovered) {
      setModifierHeld(false);
      return;
    }
    const sync = (e: KeyboardEvent) => setModifierHeld(e.metaKey || e.ctrlKey);
    const reset = () => setModifierHeld(false);
    globalThis.addEventListener("keydown", sync);
    globalThis.addEventListener("keyup", sync);
    globalThis.addEventListener("blur", reset); // focus lost mid-hold -> drop it
    return () => {
      globalThis.removeEventListener("keydown", sync);
      globalThis.removeEventListener("keyup", sync);
      globalThis.removeEventListener("blur", reset);
    };
  }, [isHovered]);

  // The hover is reported once per crossing. The callback is read through a
  // ref so a host passing a fresh closure each render does not re-fire it, and
  // a part that unmounts while hovered - it was removed, or rebuilt under the
  // pointer - reports the leave the pointer event can no longer deliver. The
  // host sees the hover the part draws: none while the camera orbits.
  const onHoverChangeRef = useRef(onHoverChange);
  onHoverChangeRef.current = onHoverChange;
  useEffect(() => {
    if (!hoverShown) return;
    onHoverChangeRef.current?.(true);
    return () => onHoverChangeRef.current?.(false);
  }, [hoverShown]);

  // Activate-only parts open a popup on click but never move, so they show no
  // drag affordances (constraint outline, guides, arrows) — only the hover glow.
  const isActivateOnly = constraint.activateOnly === true;

  // Which of the four visual layers — glow, arrows, movement area + guide
  // lines, cursor — this part draws right now. Derived by a pure function so
  // the rules can be read and tested in one place; see `dragVisuals.ts`.
  const visuals = resolveDragVisuals({
    activation: dragActivation,
    pointedOut,
    selected,
    enabled,
    showHoverHighlight,
    activateOnly: isActivateOnly,
    debug,
    isHovered,
    // Only read together with `isHovered`, so the value a part off the
    // pointer sees (always false) does not matter.
    cameraOrbiting: orbitingWhileHovered,
    modifierHeld,
    isDragging,
    draggingThis: draggingPartId === partId,
    isDragActive,
  });

  // The arrows can vanish while the pointer is still on one of them - the
  // modifier is released, or another part starts dragging. R3F removes their
  // hover records without firing `pointerleave` (there is no object left to
  // fire it on), so the hover would stay latched and the part would keep
  // glowing with the pointer over empty space. Drop it ourselves, but only when
  // the hover actually came from an arrow: a pointer on the part's own body is
  // unaffected by the arrows disappearing.
  useEffect(() => {
    if (visuals.arrows || !hoverViaArrowsRef.current) return;
    hoverViaArrowsRef.current = false;
    setHoveredArrowKey(null);
    setIsHovered(false);
  });

  // Reinforce grabbability with the cursor: `grabbing` while actively dragging
  // this part, `move` while a press on it would move it. It must stay
  // hover-bound: the cursor lives on the shared canvas, so a part that claimed
  // it without the pointer on it would show `move` across the whole viewer.
  // Uses the single-owner cursor manager so a hover hand-off between parts
  // can't leave the shared canvas cursor stuck. Releasing (leave / modifier
  // release / drag end / unmount) resets to the canvas default only while this
  // part still owns the cursor.
  const cursor = visuals.cursor;
  useEffect(() => {
    if (!cursor) return;
    const el = gl.domElement;
    acquireCanvasCursor(el, partId, cursor);
    return () => releaseCanvasCursor(el, partId);
  }, [cursor, partId, gl.domElement]);

  // Vertex pairs outlining the movement area, as `Line2` expects them: point
  // triples rather than a flat buffer, since drei's `<Line>` builds the
  // geometry itself. Worth the conversion — a `LineBasicMaterial` outline is
  // stuck at one device pixel whatever `linewidth` says (see `dragTheme.ts`).
  const constraintLinePoints = useMemo<
    [number, number, number][] | null
  >(() => {
    if (!visuals.guides) return null;

    let flat: number[] = [];

    if (constraint.type === "edge") {
      // The travel line from min to max, capped at both ends so it reads as a
      // range with two stops rather than as one more guide line.
      flat = edgeRangeSegments(bounds.min, bounds.max);
    } else if (constraint.type === "surface" && planeNormal) {
      // One rectangle outline per valid panel (drag area), so the user sees all
      // drop zones and the gap between them. Falls back to the single bounds box
      // when the constraint has no separate areas.
      const boxes =
        surfaceAreas.length > 0
          ? surfaceAreas.map((area) => area.bounds)
          : [bounds];
      flat = boxes.flatMap((box) =>
        surfaceRectSegments(box.min, box.max, planeNormal),
      );
    }

    if (flat.length < 6) return null;

    const points: [number, number, number][] = [];
    for (let i = 0; i + 2 < flat.length; i += 3) {
      const [x = 0, y = 0, z = 0] = flat.slice(i, i + 3);
      points.push([x, y, z]);
    }
    return points;
  }, [visuals.guides, constraint.type, bounds, planeNormal, surfaceAreas]);

  const constraintLineStyle = useMemo(
    () => outlineStyle(isDragging, debug),
    [isDragging, debug],
  );

  // Measured after the commit rather than during the render, and only once the
  // arrows are actually wanted: a measurement taken as the group mounts finds
  // no geometry yet and would leave every arrow sitting at the part's centre.
  // Re-taken on a rebuild, which swaps the geometries and hands down a new
  // `position`.
  const [partBox, setPartBox] = useState<Box3 | null>(null);
  const partBoxSignatureRef = useRef("");
  const remeasurePartBox = useCallback(() => {
    partBoxSignatureRef.current = meshSignature(groupNode);
    setPartBox(measurePartBox(groupNode));
  }, [groupNode]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the group is mutated in place; the position identity is what marks a rebuild
  useEffect(() => {
    if (!visuals.arrows) return;
    remeasurePartBox();
  }, [remeasurePartBox, position, visuals.arrows]);

  // Catches a model landing after the box was measured. A part's meshes are
  // swapped in by a loader mutating the group, not by a React render, so
  // neither `groupNode` nor `position` changes when the placeholder gives way
  // to the real geometry — without this the arrows keep the fit they were given
  // while the part was still downloading, until an unrelated rebuild happens to
  // re-run the effect above. Comparing a handful of geometry ids per part is
  // far cheaper than the measurement it guards, and it runs only while this
  // part's arrows are actually on screen. Mirrors `DragHighlightGlow`.
  useFrame(() => {
    if (!visuals.arrows) return;
    if (meshSignature(groupNode) === partBoxSignatureRef.current) return;
    remeasurePartBox();
  });

  // Calculate arrow directions based on constraint type
  const arrowDirections = useMemo(() => {
    const directions: Vector3[] = [];

    if (constraint.type === "edge" && constraint.axis) {
      // Edge constraint: show arrows along the axis (both directions)
      const axis = new Vector3(
        constraint.axis[0],
        constraint.axis[1],
        constraint.axis[2],
      ).normalize();
      directions.push(axis.clone());
      directions.push(axis.clone().negate());
    } else if (constraint.type === "surface" && planeNormal) {
      // Surface constraint: show arrows aligned with world axes that lie in the plane
      const normal = planeNormal.clone().normalize();
      const worldAxes = [
        new Vector3(1, 0, 0), // X
        new Vector3(0, 1, 0), // Y
        new Vector3(0, 0, 1), // Z
      ];

      for (const axis of worldAxes) {
        // Check if axis is parallel to normal (dot product close to ±1)
        const dot = Math.abs(normal.dot(axis));
        if (dot < 0.9) {
          // Project axis onto plane: axis - (axis · normal) * normal
          const projected = axis
            .clone()
            .sub(normal.clone().multiplyScalar(axis.dot(normal)))
            .normalize();
          directions.push(projected);
          directions.push(projected.clone().negate());
        }
      }
    }

    return directions;
  }, [constraint.type, constraint.axis, planeNormal]);

  // While an arrow drag runs, only the grabbed axis is still on offer: showing
  // the cross would advertise a sideways move the drag is refusing to make.
  const visibleArrowDirections = useMemo(
    () =>
      lockedAxis
        ? arrowDirections.filter((dir) => isAxisParallelTo(dir, lockedAxis))
        : arrowDirections,
    [arrowDirections, lockedAxis],
  );

  // Compute current display position (drag position or original)
  const displayPosition = dragPosition || position;

  return (
    <group>
      {/* The draggable part */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Three.js group element, not DOM */}
      <group
        ref={setGroupRef}
        position={[displayPosition.x, displayPosition.y, displayPosition.z]}
        rotation={rotation ? [rotation.x, rotation.y, rotation.z] : undefined}
        onPointerDown={enabled ? handlePointerDown : undefined}
        onPointerEnter={
          enabled && showHoverHighlight ? handlePointerEnter : undefined
        }
        onPointerMove={
          enabled && showHoverHighlight ? handlePointerMove : undefined
        }
        onPointerLeave={enabled ? handlePointerLeave : undefined}
        onClick={handleClick}
      >
        {children}
      </group>

      {/* Direction arrows - rendered at part position but WITHOUT rotation
          (world-aligned), and grabbable: a press on one drags the part along
          that arrow's axis alone. The group carries the hover and click
          handlers so an arrow keeps the part engaged (and so the click that
          concludes an arrow drag is swallowed here rather than reaching the
          lid's toggle behind it); the per-arrow press carries the axis. */}
      {visuals.arrows && (
        // biome-ignore lint/a11y/noStaticElementInteractions: Three.js group element, not DOM
        <group
          position={[displayPosition.x, displayPosition.y, displayPosition.z]}
          onPointerLeave={enabled ? handlePointerLeave : undefined}
          onClick={handleClick}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          {visibleArrowDirections.map((direction) => {
            // Create a stable key from direction components
            const dirKey = `${direction.x.toFixed(2)}_${direction.y.toFixed(2)}_${direction.z.toFixed(2)}`;
            // Start the arrow clear of the part, so the part cannot hide its
            // own direction hint now that the arrows are depth-tested.
            const outset =
              partReachAlong(partBox, displayPosition, direction) +
              ARROW_CLEARANCE_MM;
            return (
              <Arrow3D
                key={`arrow-${dirKey}`}
                direction={direction}
                outset={outset}
                // Bridge exactly the clearance the outset just added, so the
                // arrow's hit area meets the part's without overlapping it.
                grabReachBack={ARROW_CLEARANCE_MM}
                // The hover tint is suppressed for the duration of a drag: the
                // part travels under a stationary cursor, so a body drag would
                // otherwise light up whichever arrow happened to slide beneath
                // it. During an arrow drag the lock does the highlighting.
                active={
                  lockedAxis !== null ||
                  (!isDragging && hoveredArrowKey === dirKey)
                }
                onPointerDown={
                  enabled
                    ? (event) => handlePointerDown(event, direction)
                    : undefined
                }
                onPointerEnter={
                  enabled && showHoverHighlight
                    ? (event) => handleArrowPointerEnter(event, dirKey)
                    : undefined
                }
                onPointerMove={
                  enabled && showHoverHighlight
                    ? (event) => handleArrowPointerEnter(event, dirKey)
                    : undefined
                }
                onPointerLeave={
                  enabled ? () => handleArrowPointerLeave(dirKey) : undefined
                }
              />
            );
          })}
        </group>
      )}

      {/* Display values during drag */}
      {displayValues && (
        <Html
          position={[
            displayPosition.x,
            displayPosition.y + 80,
            displayPosition.z,
          ]}
          center
          style={{
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <div
            style={{
              background: "rgba(0, 0, 0, 0.8)",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              display: "flex",
              gap: "8px",
            }}
          >
            {displayValues.primary && (
              <span>
                {displayValues.primary.label}: {displayValues.primary.value}
                {displayValues.primary.unit}
              </span>
            )}
            {displayValues.secondary && (
              <span>
                {displayValues.secondary.label}: {displayValues.secondary.value}
                {displayValues.secondary.unit}
              </span>
            )}
          </div>
        </Html>
      )}

      {/* Constraint visualization (on an engaged hover, during drag, or debug) */}
      {visuals.guides && constraintLinePoints && (
        <CasedLine
          segments
          points={constraintLinePoints}
          style={constraintLineStyle}
          renderOrder={OUTLINE_RENDER_ORDER}
        />
      )}

      {/* Center guide lines (+ diagonal snap lines) for surface drags. Shown
          while the part is engaged — hovered with a drag armed, or being
          dragged — so an armed case does not fill up with lines. One center
          cross per panel in both alignment modes; diagonal snaps stay global. */}
      {visuals.guides && allGuideSegments.length > 0 && (
        <DragGuideLines segments={allGuideSegments} />
      )}

      {/* Debug: Show bounds corners */}
      {debug && (
        <group {...({ userData: { isDragHelper: true } } as WithUserData)}>
          {/* Min corner */}
          <mesh
            position={[bounds.min.x, bounds.min.y, bounds.min.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0xff0000}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Max corner */}
          <mesh
            position={[bounds.max.x, bounds.max.y, bounds.max.z]}
            renderOrder={999}
          >
            <boxGeometry args={[5, 5, 5]} />
            <meshBasicMaterial
              color={0x00ff00}
              opacity={0.8}
              transparent
              depthTest={false}
            />
          </mesh>
          {/* Current position marker */}
          <mesh
            position={[position.x, position.y, position.z]}
            renderOrder={999}
          >
            <sphereGeometry args={[8, 16, 16]} />
            <meshBasicMaterial
              color={0x0000ff}
              opacity={0.5}
              transparent
              depthTest={false}
            />
          </mesh>
        </group>
      )}

      {/* Per-panel center snap points: one marker per panel's snap center,
          highlighted for the panel the part is currently over. */}
      {(isDragging || debug) &&
        areaGuides.map((guide, areaIdx) => {
          const isActive = areaIdx === activeAreaIndex;
          return (
            <SnapMarker
              key={`area-center-${guide.key}`}
              position={guide.center}
              radius={isActive ? 6 : 5}
              color={isActive ? DRAG_COLORS.snap : DRAG_COLORS.guide}
              opacity={isActive ? 1 : 0.8}
            />
          );
        })}

      {/* Snap anchor points (during drag or debug). A surface's "Center" anchor
          is already shown as the panel marker above, resolved per panel. */}
      {(isDragging || debug) &&
        constraint.type !== "surface" &&
        constraint.snapAnchors.map((anchor) => {
          const anchorPos = toVector3(anchor.position);
          const anchorIsHovered = hoveredAnchor === anchor;
          return (
            <SnapMarker
              key={`anchor-${anchor.position.join(",")}`}
              position={anchorPos}
              radius={anchorIsHovered ? 8 : 5}
              color={anchorIsHovered ? DRAG_COLORS.snap : DRAG_COLORS.guide}
              opacity={anchorIsHovered ? 1 : 0.8}
            />
          );
        })}

      {/* Debug: Show drag position if dragging */}
      {debug && dragPosition && (
        <mesh
          position={[dragPosition.x, dragPosition.y, dragPosition.z]}
          renderOrder={999}
          {...({ userData: { isDragHelper: true } } as WithUserData)}
        >
          <sphereGeometry args={[10, 16, 16]} />
          <meshBasicMaterial
            color={0x00ffff}
            opacity={0.8}
            transparent
            depthTest={false}
          />
        </mesh>
      )}

      {/* Affordance highlight: a glow around the part. A thin outline on an
          armed part, the full tint under the pointer or when the host points
          the part out - both drawn where the case hides an armed part - and a
          plain hover glow on every other part. */}
      {visuals.glow !== "none" && (
        <DragHighlightGlow
          target={groupNode}
          refitKey={displayPosition}
          variant={visuals.glow}
          xray={visuals.glowXray}
          occlusionGroup={constraint.sourceOptionUuid || partId}
        />
      )}
    </group>
  );
};

export default DraggablePart;
